import { Select, Tooltip, Tag } from 'antd';
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import S2MosaicFinder from '../finders/S2MosaicFinder/S2MosaicFinder';
import S2MosaicQuarterlyFinder from '../finders/S2MosaicQuarterlyFinder/S2MosaicQuarterlyFinder';
import LandsatMosaicFinder from '../finders/LandsatMosaicFinder/LandsatMosaicFinder';
import PlanetFinder from '../finders/PlanetFinder/PlanetFinder';
import S2SingleFinder from '../finders/S2SingleFinder/S2SingleFinder';
import LandsatFinder from '../finders/LandsatFinder/LandsatFinder';
import styled from 'styled-components';
import { useAtom } from 'jotai';
import { showSatelliteImagePanelAtom } from '~/components/Map/stores/mapStore';
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from '../../../PanelStyle';
import { FaXmark } from 'react-icons/fa6';
import { InfoCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { satelliteRecordCountAtom, satelliteFilterTriggerAtom, currentFinderTypeAtom, SatelliteTypeEnum } from '../store';
import { Rnd } from 'react-rnd';
import s2MosaicThumb from '~/assets/images/satellite_image_preview/sentinel-s2-l2a-mosaic-120.png';
import s2SingleThumb from '~/assets/images/satellite_image_preview/sentinel-2-l2a.png';
import landsatThumb from '~/assets/images/satellite_image_preview/landsat-8-l2.png';
import planetThumb from '~/assets/images/satellite_image_preview/planetscope.png';

/**
 * A panel that allows the user to select a type of satellite image to search for.
 * It then displays the corresponding finder panel for that type.
 */
const SatelliteImagePanel: React.FC = () => {
  const { t } = useTranslation();

  const [showSatelliteImagePanel, setShowSatelliteImagePanel] = useAtom(showSatelliteImagePanelAtom);
  const [selectedType, setSelectedType] = useAtom(currentFinderTypeAtom);

  // Get record count and filter trigger from atoms
  const [recordCount] = useAtom(satelliteRecordCountAtom);
  const [filterTrigger] = useAtom(satelliteFilterTriggerAtom);

  // Options for the selector with thumbnail, title and description
  const options = [
    {
      value: SatelliteTypeEnum.S2_MOSAIC,
      selectedLabel: (
        <UI_SelectedOption>
          <img src={s2MosaicThumb} alt="Ghép S2" />
          <span>{t('satellite_image_panel.s2_mosaic', 'Ghép S2')}</span>
        </UI_SelectedOption>
      ),
      label: (
        <UI_DropdownOptionItem>
          <UI_DropdownOptionThumb>
            <img src={s2MosaicThumb} alt="Ghép S2" />
          </UI_DropdownOptionThumb>
          <UI_DropdownOptionContent>
            <UI_DropdownOptionHeader>
              <span className="title">{t('satellite_image_panel.s2_mosaic', 'Ghép S2')}</span>
              <Tag color="#722ed1" className="tag">10m</Tag>
            </UI_DropdownOptionHeader>
            <span className="desc">
              {t(
                'satellite_image_panel.s2_mosaic_description',
                'Ảnh Sentinel-2 tổng hợp lọc mây độ phân giải 10m theo năm, tháng, ngày'
              )}
            </span>
          </UI_DropdownOptionContent>
        </UI_DropdownOptionItem>
      ),
    },
    {
      value: SatelliteTypeEnum.S2_MOSAIC_QUARTERLY,
      selectedLabel: (
        <UI_SelectedOption>
          <img src={s2MosaicThumb} alt="S2 Ghép Quý" />
          <span>{t('satellite_image_panel.s2_mosaic_quarterly', 'S2 Ghép Quý')}</span>
        </UI_SelectedOption>
      ),
      label: (
        <UI_DropdownOptionItem>
          <UI_DropdownOptionThumb>
            <img src={s2MosaicThumb} alt="S2 Ghép Quý" />
          </UI_DropdownOptionThumb>
          <UI_DropdownOptionContent>
            <UI_DropdownOptionHeader>
              <span className="title">{t('satellite_image_panel.s2_mosaic_quarterly', 'Sentinel-2 Ghép Quý')}</span>
              <Tag color="#722ed1" className="tag">CDSE • 10m</Tag>
            </UI_DropdownOptionHeader>
            <span className="desc">
              {t(
                'satellite_image_panel.s2_mosaic_quarterly_description',
                'Ảnh Sentinel-2 tổng hợp ghép theo quý (leastCC) từ Copernicus Data Space'
              )}
            </span>
          </UI_DropdownOptionContent>
        </UI_DropdownOptionItem>
      ),
    },
    {
      value: SatelliteTypeEnum.LANDSAT_MOSAIC,
      selectedLabel: (
        <UI_SelectedOption>
          <img src={landsatThumb} alt="Landsat Ghép (CDSE)" />
          <span>{t('satellite_image_panel.landsat_mosaic', 'Landsat Ghép')}</span>
        </UI_SelectedOption>
      ),
      label: (
        <UI_DropdownOptionItem>
          <UI_DropdownOptionThumb>
            <img src={landsatThumb} alt="Landsat Ghép" />
          </UI_DropdownOptionThumb>
          <UI_DropdownOptionContent>
            <UI_DropdownOptionHeader>
              <span className="title">{t('satellite_image_panel.landsat_mosaic', 'Landsat Ghép (CDSE)')}</span>
              <Tag color="#fa8c16" className="tag">CDSE • 30m</Tag>
            </UI_DropdownOptionHeader>
            <span className="desc">
              {t(
                'satellite_image_panel.landsat_mosaic_description',
                'Ảnh Landsat tổng hợp ghép định kỳ 2 tháng lọc mây từ Copernicus Data Space (1997 - nay)'
              )}
            </span>
          </UI_DropdownOptionContent>
        </UI_DropdownOptionItem>
      ),
    },
    {
      value: SatelliteTypeEnum.S2_SINGLE,
      selectedLabel: (
        <UI_SelectedOption>
          <img src={s2SingleThumb} alt="Cảnh S2" />
          <span>{t('satellite_image_panel.s2_scene', 'Cảnh S2')}</span>
        </UI_SelectedOption>
      ),
      label: (
        <UI_DropdownOptionItem>
          <UI_DropdownOptionThumb>
            <img src={s2SingleThumb} alt="Cảnh S2" />
          </UI_DropdownOptionThumb>
          <UI_DropdownOptionContent>
            <UI_DropdownOptionHeader>
              <span className="title">{t('satellite_image_panel.s2_scene', 'Cảnh S2')}</span>
              <Tag color="#1677ff" className="tag">L2A • 10m</Tag>
            </UI_DropdownOptionHeader>
            <span className="desc">
              {t(
                'satellite_image_panel.s2_single_description',
                'Cảnh ảnh Sentinel-2 đơn nguyên bản, chu kỳ 5 ngày, không lọc mây'
              )}
            </span>
          </UI_DropdownOptionContent>
        </UI_DropdownOptionItem>
      ),
    },
    {
      value: SatelliteTypeEnum.PLANET,
      selectedLabel: (
        <UI_SelectedOption>
          <img src={planetThumb} alt="Planet" />
          <span>{t('satellite_image_panel.mosaic', 'Planet')}</span>
        </UI_SelectedOption>
      ),
      label: (
        <UI_DropdownOptionItem>
          <UI_DropdownOptionThumb>
            <img src={planetThumb} alt="Planet" />
          </UI_DropdownOptionThumb>
          <UI_DropdownOptionContent>
            <UI_DropdownOptionHeader>
              <span className="title">{t('satellite_image_panel.mosaic', 'Planet')}</span>
              <Tag color="#13c2c2" className="tag">PlanetScope • 3m</Tag>
            </UI_DropdownOptionHeader>
            <span className="desc">
              {t(
                'satellite_image_panel.planet_description',
                'Dữ liệu ảnh vệ tinh Planet / PlanetScope độ phân giải cao 3m theo tháng và quý'
              )}
            </span>
          </UI_DropdownOptionContent>
        </UI_DropdownOptionItem>
      ),
    },
    {
      value: SatelliteTypeEnum.LANDSAT,
      selectedLabel: (
        <UI_SelectedOption>
          <img src={landsatThumb} alt="Landsat" />
          <span>{t('satellite_image_panel.landsat', 'Landsat')}</span>
        </UI_SelectedOption>
      ),
      label: (
        <UI_DropdownOptionItem>
          <UI_DropdownOptionThumb>
            <img src={landsatThumb} alt="Landsat" />
          </UI_DropdownOptionThumb>
          <UI_DropdownOptionContent>
            <UI_DropdownOptionHeader>
              <span className="title">{t('satellite_image_panel.landsat', 'Landsat')}</span>
              <Tag color="#fa8c16" className="tag">30m</Tag>
            </UI_DropdownOptionHeader>
            <span className="desc">
              {t(
                'satellite_image_panel.landsat_description',
                'Cảnh ảnh Landsat 8/9 qua Google Earth Engine, dữ liệu lưu trữ lịch sử'
              )}
            </span>
          </UI_DropdownOptionContent>
        </UI_DropdownOptionItem>
      ),
    },
  ];


  const renderSelectedPanel = () => {
    switch (selectedType) {
      case SatelliteTypeEnum.S2_MOSAIC:
        return <S2MosaicFinder />;
      case SatelliteTypeEnum.S2_MOSAIC_QUARTERLY:
        return <S2MosaicQuarterlyFinder />;
      case SatelliteTypeEnum.LANDSAT_MOSAIC:
        return <LandsatMosaicFinder />;
      case SatelliteTypeEnum.S2_SINGLE:
        return <S2SingleFinder />;
      case SatelliteTypeEnum.PLANET:
        return <PlanetFinder />;
      case SatelliteTypeEnum.LANDSAT:
        return <LandsatFinder />;
      default:
        return <div>{t('satellite_image_panel.select_panel_type', 'Please select an image type.')}</div>;
    }
  };

  const renderDescription = () => {
    switch (selectedType) {
      case SatelliteTypeEnum.S2_MOSAIC:
        return t(
          'satellite_image_panel.s2_mosaic_description',
          'Ảnh Sentinel 2 bỏ bớt mây và ghép theo năm, tháng, ngày',
        );
      case SatelliteTypeEnum.S2_MOSAIC_QUARTERLY:
        return t(
          'satellite_image_panel.s2_mosaic_quarterly_description',
          'Ảnh Sentinel-2 tổng hợp ghép theo quý lọc mây từ Copernicus Data Space Ecosystem (CDSE)',
        );
      case SatelliteTypeEnum.LANDSAT_MOSAIC:
        return t(
          'satellite_image_panel.landsat_mosaic_description',
          'Ảnh Landsat tổng hợp ghép định kỳ 2 tháng lọc mây từ Copernicus Data Space Ecosystem (CDSE), 1997 - nay',
        );
      case SatelliteTypeEnum.S2_SINGLE:
        return t(
          'satellite_image_panel.s2_single_description',
          'Cảnh ảnh Sentinel 2, không lọc mây, không ghép ảnh, không lọc mây ',
        );
      case SatelliteTypeEnum.PLANET:
        return t('satellite_image_panel.planet_description', 'Ảnh ghép từ nguồn Planet');
      case SatelliteTypeEnum.LANDSAT:
        return t('satellite_image_panel.landsat_description', 'Cảnh ảnh Landsat qua Google Earth Engine');
      default:
        return t('satellite_image_panel.select_panel_type', 'Chọn loại ảnh.');
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const initialWidth = isMobile ? window.innerWidth : Math.min(Math.max(window.innerWidth * 0.6, 600), 1200);
  const initialHeight = isMobile ? window.innerHeight : Math.min(Math.max(window.innerHeight * 0.8, 450), 900);
  const initialX = isMobile ? 0 : Math.max(0, (window.innerWidth - initialWidth) / 2);
  const initialY = isMobile ? 0 : Math.max(0, (window.innerHeight - initialHeight) / 2);

  return (
    <Rnd
      default={{
        x: initialX,
        y: initialY,
        width: initialWidth,
        height: initialHeight,
      }}
      minWidth={isMobile ? window.innerWidth : 500}
      minHeight={isMobile ? window.innerHeight : 350}
      dragHandleClassName="draggable-header"
      cancel=".no-drag, .no-drag *, .ant-select, .ant-select *, .ant-btn, .ant-btn *, button, input, svg, path"
      bounds="window"
      enableResizing={!isMobile}
      disableDragging={isMobile}
      style={{ zIndex: 1000 }}
    >
      <UI_Wrapper>
        <UI_Header className="draggable-header" style={{ cursor: isMobile ? 'default' : 'move', userSelect: 'none' }}>
          <UI_HeaderLeft className="no-drag" style={{ cursor: 'default' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ display: 'none' }}>Tìm ảnh vệ tinh</span>
              <Select
                value={selectedType}
                onChange={setSelectedType}
                options={options}
                optionLabelProp="selectedLabel"
                popupMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 380 }}
                style={{ width: 230 }}
                placeholder={t('satellite_image_panel.select_image_type', 'Select image type')}
              />
              <Tooltip title={renderDescription()}>
                <InfoCircleOutlined style={{ color: '#666', fontSize: '16px' }} />
              </Tooltip>
              {/* Record count display - desktop */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#666', fontSize: '14px' }}>
                <span>Số bản ghi: </span>
                <span>{recordCount}</span>
              </div>
            </div>
          </UI_HeaderLeft>
          <UI_HeaderCenter style={{ flex: 1, height: '100%', minWidth: '20px' }} />
          <UI_HeaderRight className="no-drag" style={{ cursor: 'default' }}>
            <UI_HeaderRight_item>
              {/* Filter button - triggers the active finder's filter panel via atom */}
              <Tooltip title={t('satellite_image_panel.filter', 'Lọc / Tìm kiếm')}>
                <FilterOutlined
                  style={{ cursor: 'pointer', fontSize: '16px' }}
                  onClick={() => {
                    if (filterTrigger) {
                      filterTrigger();
                    }
                  }}
                />
              </Tooltip>
            </UI_HeaderRight_item>

            <UI_HeaderRight_item>
              <FaXmark onClick={() => setShowSatelliteImagePanel(false)} />
            </UI_HeaderRight_item>
          </UI_HeaderRight>
        </UI_Header>
        <UI_Content>
          <div style={{ padding: '16px', width: '100%', height: '100%' }}>{renderSelectedPanel()}</div>
        </UI_Content>
      </UI_Wrapper>
    </Rnd>
  );
};

export default memo(SatelliteImagePanel);

const UI_SelectedOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  img {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    object-fit: cover;
    border: 1px solid #e0e0e0;
    flex-shrink: 0;
  }

  span {
    font-weight: 500;
    font-size: 13px;
    color: #262626;
  }
`;

const UI_DropdownOptionItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 6px 2px;
`;

const UI_DropdownOptionThumb = styled.div`
  width: 52px;
  height: 52px;
  min-width: 52px;
  max-width: 52px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const UI_DropdownOptionContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  .desc {
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.35;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const UI_DropdownOptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .title {
    font-weight: 600;
    font-size: 14px;
    color: #1f1f1f;
  }

  .tag {
    font-size: 10px;
    line-height: 16px;
    padding: 0 4px;
    margin: 0;
    border-radius: 3px;
    font-weight: 500;
  }
`;

const UI_Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadows-paneShadow);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    border-radius: 0;
  }
`;

const UI_HeaderCenter = styled.div`
  flex: 1;
  height: 100%;
`;

const UI_Content = styled.div`
  padding: 8px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  gap: 8px;
`;